import type { GetStaticProps } from 'next';
import React from 'react';

import { csrClient } from '../apollo/api/avantArteClient';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import Layout from '../components/Layout';
import GiftCards from '../components/PageTypes/GiftCards';
import AvantArteAuthGateway from '../gateways/AvantArteAuthGateway';
import { ShopifyCheckoutGateway } from '../gateways/ShopifyCheckoutGateway';
import { ShopifyGiftCardsGateway } from '../gateways/ShopifyGiftCardGateway';
import { imageOgStaticLoader } from '../helpers/og-image';
import { GiftCardVariantProduct } from '../types/Purchasing';
import { getStoryblokPage } from '../utils/storyblok';
import { pageTranslations } from '../utils/translations';

const giftCardsGateway = new ShopifyGiftCardsGateway(storefrontClient);
const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);
const authGateway = new AvantArteAuthGateway(csrClient, storefrontClient);

const Page = ({ locale, locales, ogImageUrl, pageData, story }: PageProps) => {
  return (
    <Layout
      description={story.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={story.content.seo?.title}
    >
      <GiftCards
        authGateway={authGateway}
        blok={story.content}
        checkoutGateway={checkoutGateway}
        giftCardVariantProducts={pageData as GiftCardVariantProduct[]}
      />
    </Layout>
  );
};

export default Page;

export const getStaticProps: GetStaticProps = async ({
  locale,
  locales,
  preview = true,
}) => {
  const giftCards = await giftCardsGateway.getGiftCards();

  const page = await getStoryblokPage('cdn/stories/gift-cards', {
    locale,
    preview,
  });

  if (page === null) {
    throw new Error('Storyblok content not found');
  }

  if (giftCards === null) {
    return {
      notFound: true,
    };
  }

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: { key: 'og:giftCards.title' },
    imageSrc: (page.data.story.content as GiftCardPageType).banner?.filename
      ? (page.data.story.content as GiftCardPageType).banner.filename
      : 'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      pageData: giftCards,
      story: page.data ? page.data.story : false,
      preview,
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale!, ['giftCards', 'og'])),
    },
  };
};
